import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button, Box, Typography, Grid, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Chip
} from '@material-ui/core'
import { ArrowBack, Warning as WarningIcon } from '@material-ui/icons'

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
const pct = (v) => `${((v || 0) * 100).toFixed(1)}%`
const marginColor = (m) => m >= 0.20 ? '#2e7d32' : m >= 0.10 ? '#ff9800' : '#f44336'

const cardStyle = {
  backgroundColor: '#fff', borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 16, marginBottom: 16
}

const EstimateReviewView = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const { url } = props
  const [dashboard, setDashboard] = React.useState(null)
  const [moduleRollup, setModuleRollup] = React.useState(null)
  const [staleMaterials, setStaleMaterials] = React.useState(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    setLoading(true)
    Promise.all([
      axios.get(`${url}/api/bid-estimation/projects/${projectId}/dashboard`),
      axios.get(`${url}/api/bid-estimation/projects/${projectId}/module-rollup`),
      axios.get(`${url}/api/bid-estimation/projects/${projectId}/stale-materials`).catch(() => ({ data: null }))
    ]).then(([dashRes, rollupRes, staleRes]) => {
      setDashboard(dashRes.data)
      setModuleRollup(rollupRes.data)
      setStaleMaterials(staleRes.data)
      setLoading(false)
    }).catch(err => {
      console.error('Failed to load estimate review:', err)
      setLoading(false)
    })
  }, [projectId])

  if (loading) {
    return (
      <div style={{ backgroundColor: '#fafafa', minHeight: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  const summary = dashboard?.summary || {}
  const byCostType = dashboard?.byCostType || {}
  const modules = moduleRollup?.modules || []

  return (
    <div style={{ backgroundColor: '#fafafa', minHeight: '100%' }}>
      {/* Header */}
      <Box style={{
        backgroundColor: '#2e7d32', color: '#fff',
        padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12
      }}>
        <Button size="small" startIcon={<ArrowBack />}
          onClick={() => history.push(`/bid-estimation/${projectId}`)}
          style={{ color: '#fff', textTransform: 'none' }}>
          Back
        </Button>
        <Typography variant="h6" style={{ fontWeight: 600, color: '#fff' }}>
          Estimate Review — {dashboard?.project?.name || ''}
        </Typography>
      </Box>

      <div style={{ padding: 20 }}>
        {/* Project Summary */}
        <div style={cardStyle}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">Total Cost</Typography>
              <Typography variant="h6" style={{ fontWeight: 700 }}>{fmt(summary.totalCost)}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">Total Sell</Typography>
              <Typography variant="h6" style={{ fontWeight: 700 }}>{fmt(summary.totalSell)}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">Margin</Typography>
              <Typography variant="h6" style={{ fontWeight: 700, color: marginColor(summary.margin) }}>
                {pct(summary.margin)}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">Coverage</Typography>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                {summary.coveredItems || 0}/{summary.totalItems || 0}
              </Typography>
            </Grid>
          </Grid>
        </div>

        {/* Module Breakdown */}
        <div style={cardStyle}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>Module Breakdown</Typography>
          {modules.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No modules — assign line items to modules to see breakdown.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Covered</TableCell>
                  <TableCell align="right">Missing</TableCell>
                  <TableCell align="right">Total Cost</TableCell>
                  <TableCell align="right">Total Sell</TableCell>
                  <TableCell align="right">Margin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map(m => (
                  <TableRow key={m._id}>
                    <TableCell style={{ fontWeight: 500 }}>{m.scope}</TableCell>
                    <TableCell align="right">{m.itemCount}</TableCell>
                    <TableCell align="right">{m.coveredCount}</TableCell>
                    <TableCell align="right">{m.missingCount}</TableCell>
                    <TableCell align="right">{fmt(m.totalCost)}</TableCell>
                    <TableCell align="right">{fmt(m.totalSell)}</TableCell>
                    <TableCell align="right" style={{ color: marginColor(m.margin), fontWeight: 600 }}>
                      {pct(m.margin)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Cost Type Breakdown */}
        <div style={cardStyle}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>Cost Type Breakdown</Typography>
          <Grid container spacing={2}>
            {Object.entries(byCostType).map(([type, data]) => (
              <Grid item xs={6} sm={4} md={2} key={type}>
                <div style={{ textAlign: 'center', padding: 8, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                  <Typography variant="caption" color="textSecondary" style={{ textTransform: 'capitalize' }}>
                    {type}
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>{fmt(data.cost)}</Typography>
                  <Typography variant="caption" color="textSecondary">Sell: {fmt(data.sell)}</Typography>
                </div>
              </Grid>
            ))}
          </Grid>
        </div>

        {/* Warnings */}
        {(summary.missingItems > 0 || (staleMaterials && staleMaterials.count > 0)) && (
          <div style={cardStyle}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>Warnings</Typography>
            {summary.missingItems > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <WarningIcon style={{ color: '#f44336', fontSize: 18 }} />
                <Typography variant="body2">
                  {summary.missingItems} items missing pricing —{' '}
                  <span
                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => history.push(`/bid-estimation/${projectId}/items?coverageStatus=missing`)}
                  >
                    Go to Line Items
                  </span>
                </Typography>
              </div>
            )}
            {staleMaterials && staleMaterials.count > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <WarningIcon style={{ color: '#ff9800', fontSize: 18 }} />
                <Typography variant="body2">
                  {staleMaterials.count} catalog items have quotes older than {staleMaterials.stalePricingDays} days —{' '}
                  <span
                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => history.push('/bid-estimation/rates')}
                  >
                    Go to Rate Tables
                  </span>
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(EstimateReviewView)
